import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import type {IssueFieldAddedEvent$key} from './__generated__/IssueFieldAddedEvent.graphql'
import type {IssueFieldChangedEvent$key} from './__generated__/IssueFieldChangedEvent.graphql'
import type {IssueFieldRemovedEvent$key} from './__generated__/IssueFieldRemovedEvent.graphql'
import {IssueFieldEvent, type IssueFieldEventBaseProps, normalizeMultiSelectOption} from './IssueFieldEvent'
import {RolledupIssueFieldEvent} from './RolledupIssueFieldEvent'

type IssueFieldEventKey = IssueFieldAddedEvent$key | IssueFieldChangedEvent$key | IssueFieldRemovedEvent$key

type IssueFieldAddedEventProps = IssueFieldEventBaseProps & {
  queryRef: IssueFieldAddedEvent$key & {createdAt?: string}
  rollupGroup?: Record<string, IssueFieldEventKey[]>
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueFieldAddedEventFragment = graphql`
  fragment IssueFieldAddedEvent on IssueFieldAddedEvent {
    databaseId
    actor {
      avatarUrl(size: 64)
      login
      profileResourcePath
      __typename
      ... on Bot {
        isCopilot
        isAgent
        displayName
      }
      ... on User {
        name
      }
      ... on EnterpriseUserAccount {
        name
      }
      ... on Mannequin {
        name
      }
    }
    createdAt
    issueField {
      ... on IssueFieldText {
        dataType
        name
      }
      ... on IssueFieldDate {
        name
        dataType
      }
      ... on IssueFieldNumber {
        name
        dataType
      }
      ... on IssueFieldSingleSelect {
        name
        dataType
      }
      ... on IssueFieldMultiSelect {
        name
        dataType
      }
    }
    options {
      name
      color
    }
    value
    color
    intent {
      rationale
    }
  }
`

export function IssueFieldAddedEvent({queryRef, rollupGroup, ...props}: IssueFieldAddedEventProps) {
  const {
    actor,
    createdAt,
    issueField,
    options,
    value: newValue,
    databaseId,
    color,
    intent,
  } = useFragment(IssueFieldAddedEventFragment, queryRef)
  const fieldOptions = options?.map(option => normalizeMultiSelectOption(option.name, option.color))

  const rollupContent = rollupGroup ? (
    <RolledupIssueFieldEvent rollupGroup={rollupGroup} repositoryNameWithOwner={props.repositoryNameWithOwner} />
  ) : undefined

  return (
    <IssueFieldEvent
      actor={actor}
      createdAt={createdAt}
      fieldName={issueField?.name}
      dataType={issueField?.dataType}
      fieldValue={newValue}
      fieldOptions={fieldOptions}
      color={color}
      databaseId={databaseId}
      leadingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldAdded.leading}
      trailingLabel={rollupGroup ? undefined : LABELS.timeline.issueFieldAdded.trailing}
      hasValue={!rollupGroup}
      rollupContent={rollupContent}
      rationale={rollupGroup ? undefined : (intent?.rationale ?? undefined)}
      analyticsAction="ISSUE_FIELD_ADDED"
      {...props}
    />
  )
}
